import type { ApiError } from "./client";
import { useResource } from "./useResource";

/**
 * The arguments a tool was called with (`api.md` §Chat). The transcript window
 * carries only `argument_summary` — one truncated line — because these are
 * unbounded: a `Write` call's arguments are a whole file. So they are fetched
 * for the one row a reader expanded, and nowhere else.
 *
 * `null` for a call recorded without any, which is not the same as `{}`.
 */
export type ToolCallArguments = Readonly<Record<string, unknown>> | null;

export type UseToolCallArguments = {
  readonly args: ToolCallArguments;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
};

/**
 * Fetch one call's arguments. Pass `publicId === undefined` to skip, matching
 * `useResource`'s null-path convention — a side thread's calls carry no id
 * (side-threads.md §Prototype scope).
 *
 * Mounted only while its row is open, so closing and re-opening costs one more
 * request. That is the right way round: a cache of every expanded call's
 * arguments is a transcript's worth of file contents held for a reader who has
 * moved on.
 */
export const useToolCallArguments = (
  publicId: string | undefined,
): UseToolCallArguments => {
  const { data, error, loading } = useResource<{
    arguments: ToolCallArguments;
  }>(publicId === undefined ? null : `/tool-calls/${publicId}`);
  return { args: data?.arguments ?? null, error, loading };
};
